<script setup lang="ts">
import { COMPETENCE_DOMAIN_ICON_OPTIONS } from '@/constants/competence-icons.constants';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import SearchInput from '@/core/elements/SearchInput.vue';
import { matches, normalizeWhitespace } from 'common/utils/string.utils';
import type { EIcon } from 'common/enum/misc.enum';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AppearanceOptionGrid from './AppearanceOptionGrid.vue';

defineProps<{ selected: EIcon | null }>();
const emit = defineEmits<{ select: [icon: EIcon] }>();
const { t } = useI18n();
const query = ref('');
const normalizedQuery = computed(() => normalizeWhitespace(query.value));
const groups = computed(() =>
  Object.entries(COMPETENCE_DOMAIN_ICON_OPTIONS).map(([key, icons]) => {
    const label = t(`competenceIconGroup.${key}`);
    return {
      key,
      label,
      options: icons
        .map((icon) => ({ value: icon, label: t(`competenceIcons.${icon}`) }))
        .filter((option) => matches(`${label} ${option.label}`, normalizedQuery.value)),
    };
  }),
);
</script>

<template>
  <div class="flex h-[354px] min-h-0 flex-col gap-sm p-md">
    <SearchInput
      v-model="query"
      :label="t('entityAppearance.search')"
      :placeholder="t('entityAppearance.search')"
      size="sm"
      class="!h-cell shrink-0 !outline-none focus-within:border-tint focus-within:ring-2 focus-within:ring-focus"
      @keydown.enter.prevent
    />
    <AppearanceOptionGrid
      :groups="groups"
      :selected="selected"
      :label="t('entityAppearance.icon')"
      :empty-label="t('entityAppearance.noResults')"
      @select="emit('select', $event)"
    >
      <template #option="{ option }"><AnyIcon :icon-name="option.value" class="size-icon-sm" /></template>
    </AppearanceOptionGrid>
  </div>
</template>
